import { computed, inject, Injectable, signal } from '@angular/core';
import { AppError } from '@/app/core/http/app-error.model';
import { toAppError } from '@/app/core/http/error-catalog';
import { buildRecentPeriods, PeriodIso } from '@/app/shared/utils/period.utils';
import {
  AggregatedReportingFilterCriteria,
  AggregatedReportingKpis,
  AggregatedReportingPeriod,
  EMPTY_AGGREGATED_REPORTING_KPIS,
} from '../models/aggregated-reporting.model';
import { CATALOG_PAGE_SIZE, PlanOption } from '../models/catalog.model';
import { AggregatedReportingRepository } from '../services/aggregated-reporting.repository';
import { RemoteCatalog } from './remote-catalog';

export type AggregatedReportingStatus = 'loading' | 'ready' | 'empty' | 'error';

export const REPORTING_MAX_MONTHS = 12;

const ERROR_SUBJECT = 'la reportería agregada';

/**
 * Ventana ofrecida en el selector: los `maxMonths` meses que terminan en el mes
 * ANTERIOR al actual. El padrón es mensual y acumula, así que la carga del mes
 * en curso todavía no corrió y ese mes vendría siempre vacío.
 *
 * `maxMonths` sale del parámetro por prestador que expone el BFF; se acota a
 * `REPORTING_MAX_MONTHS`, que es el mismo tope duro que aplica la API. Sin
 * esto, un prestador con ventana más corta elegía un rango que la API rechaza
 * con 400.
 */
export function periodOptionsFor(maxMonths: number): PeriodIso[] {
  const [, previousMonth] = buildRecentPeriods(2);
  return buildRecentPeriods(clampMonths(maxMonths), previousMonth).reverse();
}

export function defaultPeriodOptions(): PeriodIso[] {
  return periodOptionsFor(REPORTING_MAX_MONTHS);
}

function clampMonths(maxMonths: number): number {
  if (!Number.isFinite(maxMonths) || maxMonths < 1) return REPORTING_MAX_MONTHS;
  return Math.min(Math.trunc(maxMonths), REPORTING_MAX_MONTHS);
}

@Injectable()
export class AggregatedReportingStore {
  private readonly repository = inject(AggregatedReportingRepository);

  private readonly _status = signal<AggregatedReportingStatus>('loading');
  private readonly _error = signal<AppError | null>(null);
  private readonly _periods = signal<readonly AggregatedReportingPeriod[]>([]);
  private readonly _kpis = signal<AggregatedReportingKpis>(EMPTY_AGGREGATED_REPORTING_KPIS);
  private readonly _dataAsOf = signal<string | null>(null);
  private readonly _periodOptions = signal<readonly PeriodIso[]>(defaultPeriodOptions());
  private readonly _maxMonths = signal(REPORTING_MAX_MONTHS);
  private readonly _range = signal<AggregatedReportingFilterCriteria>(defaultRange());
  private catalogRange: AggregatedReportingFilterCriteria = defaultRange();

  readonly status = this._status.asReadonly();
  readonly error = this._error.asReadonly();
  readonly periods = this._periods.asReadonly();
  readonly kpis = this._kpis.asReadonly();
  readonly dataAsOf = this._dataAsOf.asReadonly();
  readonly periodOptions = this._periodOptions.asReadonly();
  readonly maxMonths = this._maxMonths.asReadonly();
  readonly range = this._range.asReadonly();
  readonly plans = new RemoteCatalog<PlanOption>((search) =>
    this.repository.getPlans({
      from: this.catalogRange.from,
      to: this.catalogRange.to,
      search,
      size: CATALOG_PAGE_SIZE,
    }),
  );

  readonly isLoading = computed(() => this._status() === 'loading');
  readonly canInteract = computed(() => this._status() !== 'loading');

  load(): void {
    this._status.set('loading');

    this.repository.getAvailablePeriods().subscribe({
      next: ({ maxMonths }) => this.searchWindow(maxMonths),
      error: () => this.searchWindow(REPORTING_MAX_MONTHS),
    });
  }

  private searchWindow(maxMonths: number): void {
    const options = periodOptionsFor(maxMonths);

    this._maxMonths.set(options.length);
    this._periodOptions.set(options);
    this.search({ from: options[0], to: options[options.length - 1] });
  }

  setCatalogRange(from: PeriodIso, to: PeriodIso): void {
    if (from === this.catalogRange.from && to === this.catalogRange.to) return;
    this.catalogRange = { from, to };
    this.plans.reset();
  }

  search(criteria: AggregatedReportingFilterCriteria): void {
    this._range.set(criteria);
    this._error.set(null);
    this._status.set('loading');

    this.repository
      .getReport({ from: criteria.from, to: criteria.to, planName: criteria.planName })
      .subscribe({
        next: (report) => {
          this._periods.set(report.periods);
          this._kpis.set(report.kpis);
          this._dataAsOf.set(report.dataAsOf ?? null);
          this._status.set(report.periods.some((period) => !period.noData) ? 'ready' : 'empty');
        },
        error: (cause: unknown) => {
          this._periods.set([]);
          this._kpis.set(EMPTY_AGGREGATED_REPORTING_KPIS);
          this._error.set(toAppError(cause, ERROR_SUBJECT));
          this._status.set('error');
        },
      });
  }
}

function defaultRange(): AggregatedReportingFilterCriteria {
  const options = defaultPeriodOptions();
  return { from: options[0], to: options[options.length - 1] };
}
